"use client";

import { FrameContext } from "@shopify/polaris";
import clsx from "clsx";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ContextType,
  type ReactNode,
  type RefObject,
} from "react";

type FrameContextValue = NonNullable<ContextType<typeof FrameContext>>;
type ToastMessage = FrameContextValue["toastMessages"][number];

export interface Logo {
  topBarSource?: string;
  contextualSaveBarSource?: string;
  url?: string;
  accessibilityLabel?: string;
  width?: number;
}

export interface FrameProps {
  logo?: Logo;
  offset?: string;
  children?: ReactNode;
  topBar?: ReactNode;
  navigation?: ReactNode;
  globalRibbon?: ReactNode;
  showMobileNavigation?: boolean;
  onNavigationDismiss?(): void;
  sidebar?: boolean;
  skipToContentTarget?: RefObject<HTMLAnchorElement>;
}

const DEFAULT_TOAST_DURATION = 5000;

function ToastView({
  toast,
  onDismiss,
}: {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const duration = toast.duration ?? DEFAULT_TOAST_DURATION;
    if (duration <= 0) return;
    const t = setTimeout(() => onDismiss(toast.id), duration);
    return () => clearTimeout(t);
  }, [toast.id, toast.duration, onDismiss, toast]);

  return (
    <div
      className={clsx(
        "pointer-events-auto flex items-center gap-3 rounded-md px-4 py-3 text-sm text-white shadow-lg",
        toast.error ? "bg-red-600" : "bg-gray-900 dark:bg-gray-700",
      )}
      role="status"
    >
      <span className="flex-1">{toast.content}</span>
      {toast.action ? (
        <a
          href={toast.action.url}
          target={toast.action.external ? "_blank" : undefined}
          rel={toast.action.external ? "noreferrer" : undefined}
          onClick={() => {
            toast.action?.onAction?.();
            onDismiss(toast.id);
          }}
          className="font-semibold underline underline-offset-2"
        >
          {toast.action.content}
        </a>
      ) : null}
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 opacity-80 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}

export default function Frame({
  logo,
  offset = "0px",
  topBar,
  navigation,
  globalRibbon,
  showMobileNavigation = false,
  onNavigationDismiss,
  children,
}: FrameProps) {
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  const [loadingStack, setLoadingStack] = useState(0);

  const showToast = useCallback((toast: ToastMessage) => {
    setToastMessages((prev) =>
      prev.some((t) => t.id === toast.id) ? prev : [...prev, toast],
    );
  }, []);

  const hideToast = useCallback(({ id }: { id: string }) => {
    setToastMessages((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissToast = useCallback(
    (id: string) => {
      setToastMessages((prev) => {
        const toast = prev.find((t) => t.id === id);
        toast?.onDismiss?.();
        return prev.filter((t) => t.id !== id);
      });
    },
    [],
  );

  const startLoading = useCallback(() => {
    setLoadingStack((s) => s + 1);
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingStack((s) => Math.max(0, s - 1));
  }, []);

  const setContextualSaveBar = useCallback(() => {}, []);
  const removeContextualSaveBar = useCallback(() => {}, []);

  const frameValue = useRef({
    logo,
    showToast,
    hideToast,
    toastMessages,
    setContextualSaveBar,
    removeContextualSaveBar,
    startLoading,
    stopLoading,
  });
  frameValue.current = {
    logo,
    showToast,
    hideToast,
    toastMessages,
    setContextualSaveBar,
    removeContextualSaveBar,
    startLoading,
    stopLoading,
  };

  const hasTopBar = Boolean(topBar);
  const hasNav = Boolean(navigation);

  useEffect(() => {
    if (!showMobileNavigation) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onNavigationDismiss?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showMobileNavigation, onNavigationDismiss]);

  return (
    <FrameContext.Provider value={frameValue.current}>
      <div
        className="relative min-h-screen bg-[#f1f1f1] text-gray-900 dark:bg-gray-900 dark:text-gray-100"
        style={{ ["--pc-frame-offset" as string]: offset }}
      >
        {loadingStack > 0 ? (
          <div className="fixed inset-x-0 top-0 z-[60] h-0.5 overflow-hidden">
            <div className="h-full w-full animate-pulse bg-blue-500" />
          </div>
        ) : null}

        {hasTopBar ? (
          <div className="sticky top-0 z-30">{topBar}</div>
        ) : null}

        <div className="flex">
          {hasNav ? (
            <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 overflow-y-auto md:block">
              {navigation}
            </aside>
          ) : null}

          {showMobileNavigation ? (
            <div
              className="fixed inset-0 z-50 md:hidden"
              role="dialog"
              aria-modal="true"
            >
              <div
                className="absolute inset-0 bg-black/50"
                onClick={onNavigationDismiss}
              />
              <aside className="absolute left-0 top-0 h-full w-64 max-w-[85vw] overflow-y-auto bg-white dark:bg-gray-800">
                {navigation}
              </aside>
            </div>
          ) : null}

          <main className="min-w-0 flex-1">
            {globalRibbon}
            {children}
          </main>
        </div>

        {toastMessages.length > 0 ? (
          <div
            className="pointer-events-none fixed bottom-4 left-4 z-[70] flex flex-col gap-2"
            aria-live="assertive"
          >
            {toastMessages.map((toast) => (
              <ToastView
                key={toast.id}
                toast={toast}
                onDismiss={dismissToast}
              />
            ))}
          </div>
        ) : null}
      </div>
    </FrameContext.Provider>
  );
}
